import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const sizeClasses = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
} as const;

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /** className ของพื้นที่เนื้อหาที่ scroll ได้ */
  bodyClassName?: string;
  size?: "sm" | "md" | "lg";
  footer?: React.ReactNode;
  showCloseButton?: boolean;
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  bodyClassName,
  size = "md",
  footer,
  showCloseButton = true,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={showCloseButton}
        className={cn(
          "flex max-h-[min(92dvh,840px)] flex-col gap-0 overflow-hidden p-0",
          sizeClasses[size],
          className,
        )}
      >
        {title || description ? (
          <DialogHeader className="shrink-0 space-y-1.5 px-6 pt-6 pb-4 text-left">
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
        ) : null}
        {children != null ? (
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto overscroll-contain px-6",
              title || description ? "pb-4" : "pt-6 pb-4",
              bodyClassName,
            )}
          >
            {children}
          </div>
        ) : null}
        {footer ? (
          <DialogFooter className="shrink-0 gap-2 border-border/60 border-t bg-card/80 px-6 py-4 backdrop-blur-sm sm:space-x-0">
            {footer}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = "ຕົກລົງ",
  cancelLabel = "ຍົກເລີກ",
  confirmVariant = "default",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const [submitting, setSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            isLoading={submitting}
          >
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
